// useMembers — the member READ seam (contract 22 §9), shared by three panes: ORP-18888
// (Permissions), ORP-18886 (Notifications recipients) and ORP-18889 (the invite modal's duplicate
// detection). The WRITE half of §9's `useMembers` signature — `invite`/`update`/`remove`/`revoke`/
// `resend`/`transferOwnership` — is ORP-18888's and is deliberately absent here.
//
// State is per-call and component-local, matching contract §7 ("every pane's working data is
// component-local"). §7's `settingsStore.members` cache is the Permissions pane's + nav badge's
// concern; introducing it here would half-build a store ORP-18886 co-owns, and a module-scoped
// cache outside Pinia would survive logout with nothing to reset it.
import { computed, ref, shallowRef, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import { AccountMembersApi } from '../api/AccountMembersApi'
import type {
  AccountMemberRow,
  AccountUsersDto,
  AssistantSeats,
  MemberDashboardRef,
  MemberListRow,
  PendingInviteRow,
} from '../models/member.types'
import { isAccountMemberRow, isPendingInviteRow } from '../models/member.types'
import type { SettingsPaneState } from '../models/settingsPane.types'
import {
  assistantSeats,
  buildMemberList,
  existingEmails as foldRowEmails,
} from '../service/memberList'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError } from '@/shared/http'

export interface UseMembersOptions {
  /**
   * 09's dashboard catalog for the account, when the caller has one (CM-22-03 / BE-22-06 — it is
   * an N-call fan-out this seam does not own). Leave it out and dashboard-scoped invites stay
   * visible with unresolved `dashboards`, rather than disappearing (see `MemberListContext`).
   */
  dashboards?: MaybeRefOrGetter<readonly MemberDashboardRef[] | undefined>
  api?: AccountMembersApi
}

export interface UseMembers {
  /** Members + pending invites, one flat list, owner first. */
  rows: ComputedRef<MemberListRow[]>
  /** Accepted members only — the rows that carry a `UserId`. */
  members: ComputedRef<AccountMemberRow[]>
  /** Pending invites only — the rows that carry an `InviteId`. */
  invites: ComputedRef<PendingInviteRow[]>
  /** The account owner's row, if it is in the list. Never editable or removable (§16). */
  owner: ComputedRef<AccountMemberRow | null>
  /** Every address already on the account, folded for case-insensitive comparison. Fold a
   *  candidate with `foldEmail` from the service before testing it. */
  existingEmails: ComputedRef<Set<string>>
  /** The account's AI-assistant (MCP) seats, or `null` until a payload carrying them has loaded. */
  assistantSeats: ComputedRef<AssistantSeats | null>
  /** 22's pane vocabulary: `loading` → `ready` | `empty` | `error` | `forbidden`. */
  state: ComputedRef<SettingsPaneState>
  error: ComputedRef<ApiError | null>
  /** Fetch (or re-fetch) the account's members. Never throws — read `state`/`error`. */
  refresh: () => Promise<void>
}

export function useMembers(options: UseMembersOptions = {}): UseMembers {
  const api = options.api ?? new AccountMembersApi()
  const { accountId, accountEmail } = useAccount()
  const { workspaceId } = useWorkspace()

  const payload = shallowRef<AccountUsersDto | null>(null)
  const pending = ref(false)
  const loaded = ref(false)
  const error = ref<ApiError | null>(null)

  const rows = computed(() =>
    buildMemberList(payload.value, {
      workspaceId: workspaceId.value,
      ownerEmail: accountEmail.value,
      dashboards: toValue(options.dashboards),
    }),
  )

  const members = computed(() => rows.value.filter(isAccountMemberRow))

  const state = computed<SettingsPaneState>(() => {
    // A 403 is a first-class render, not an error banner (§12 "degraded, not broken"). It is
    // derived from the response rather than from `canManageMembers`, because the Notifications and
    // invite consumers read this list without holding the Permissions pane's gate.
    if (error.value) return error.value.kind === 'forbidden' ? 'forbidden' : 'error'
    if (pending.value || !loaded.value) return 'loading'
    return rows.value.length === 0 ? 'empty' : 'ready'
  })

  async function refresh(): Promise<void> {
    const account = accountId.value
    if (account === undefined) {
      payload.value = null
      loaded.value = false
      return
    }

    pending.value = true
    error.value = null
    try {
      payload.value = await api.listMembers(account)
      loaded.value = true
    } catch (cause) {
      error.value = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      payload.value = null
      loaded.value = false
    } finally {
      pending.value = false
    }
  }

  return {
    rows,
    members,
    invites: computed(() => rows.value.filter(isPendingInviteRow)),
    owner: computed(() => members.value.find((row) => row.isOwner) ?? null),
    existingEmails: computed(() => foldRowEmails(rows.value)),
    assistantSeats: computed(() => assistantSeats(payload.value)),
    state,
    error: computed(() => error.value),
    refresh,
  }
}
